import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { map, catchError, switchMap, withLatestFrom, mergeMap, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import {
  CountNotificationReceiptAction,
  CountNotificationReceiptErroredAction,
  CountNotificationReceiptSucceededAction,
  MarkAllNotificationReceiptsAction,
  MarkAllNotificationReceiptsErroredAction,
  MarkAllNotificationReceiptsSucceededAction,
  MenuOpenedNotificationReceiptAction,
  notificationReceiptAction,
} from './notification-receipt.actions';
import { NotificationReceiptService } from './notification-receipt.service';
import { AppStore } from '../store.model';
import { notificationReceiptSelectors } from './notification-receipt.selectors';
import { NotificationState } from 'studio-components';
import { AuthService } from '@auth0/auth0-angular';

@Injectable()
export class NotificationReceiptEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        notificationReceiptAction.load,
        notificationReceiptAction.sortChanged,
        notificationReceiptAction.paginationChanged,
        notificationReceiptAction.filterChanged,
      ),
      withLatestFrom(this.store.select(notificationReceiptSelectors.selectQuery)),
      switchMap(([, query]) =>
        this.notificationReceiptService.load(query).pipe(
          map(({ data, page, total, limit }) =>
            notificationReceiptAction.loadSucceeded({ entities: data, page, total, limit }),
          ),
          catchError((error) => of(notificationReceiptAction.loadErrored({ error }))),
        ),
      ),
    ),
  );

  getCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CountNotificationReceiptAction),
      switchMap(({ state }) =>
        this.notificationReceiptService.getCount(state).pipe(
          map(({ count }) => CountNotificationReceiptSucceededAction({ count })),
          catchError((error) => of(CountNotificationReceiptErroredAction({ error }))),
        ),
      ),
    ),
  );

  getNotificationCountOnNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      withLatestFrom(this.authService.isAuthenticated$),
      filter(([, isAuthenticated]) => isAuthenticated),
      switchMap(() =>
        this.notificationReceiptService.getCount().pipe(
          map(({ count }) => CountNotificationReceiptSucceededAction({ count })),
          catchError((error) => of(CountNotificationReceiptErroredAction({ error }))),
        ),
      ),
    ),
  );

  markAllReceiptNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkAllNotificationReceiptsAction),
      switchMap(({ state }) =>
        this.notificationReceiptService.markReceipts(state).pipe(
          map(() => MarkAllNotificationReceiptsSucceededAction()),
          catchError((error) => of(MarkAllNotificationReceiptsErroredAction({ error }))),
        ),
      ),
    ),
  );

  menuOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuOpenedNotificationReceiptAction),
      map(() => notificationReceiptAction.load()),
    ),
  );

  markAllNewNotificationReceiptAsSeen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationReceiptAction.loadSucceeded),
      mergeMap(({ entities }) => {
        const markAsSeen = !!(entities.length && entities.find((entity) => !entity.state));
        const actions: any[] = [CountNotificationReceiptSucceededAction({ count: 0 })];

        if (markAsSeen) {
          actions.push(MarkAllNotificationReceiptsAction({ state: NotificationState.Seen }));
        }

        return actions;
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private notificationReceiptService: NotificationReceiptService,
    private store: Store<AppStore>,
    private authService: AuthService,
  ) {}
}
